import React from 'react';
import { EyeIcon, LockIcon, MailIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { InitialAvatar } from '../InitialAvatar';
import { PhotoSide } from './PhotoSide';
import { PANEL_PHOTOS, RECENT_ARRIVALS, decadeFor, membersFromDecade } from '../../data/registration';
import type { RegistrationDraft } from '../../types/registration';
interface RegistrationCompleteProps {
  draft: RegistrationDraft;
  onStartOver: () => void;
  onDone?: () => void;
}
function publicName(draft: RegistrationDraft): string {
  const first = draft.firstName.trim() || 'You';
  const initial = draft.lastName.trim().charAt(0);
  return initial ? `${first} ${initial}.` : first;
}
function yearsLabel(draft: RegistrationDraft): string {
  if (draft.startYear) {
    if (draft.stillInvolved) return `${draft.startYear}–present`;
    if (!draft.endYear || draft.endYear === draft.startYear) return draft.startYear;
    return `${draft.startYear}–${draft.endYear}`;
  }
  if (draft.decades.length > 0) {
    const eras = [...draft.decades].sort().join(', ');
    return draft.stillInvolved ? `${eras} · still involved` : eras;
  }
  if (draft.stillInvolved) return 'Still involved';
  return 'Years not set';
}

/** A year to anchor the archive's response on, from years or decades. */
function anchor(draft: RegistrationDraft): string {
  if (draft.startYear) return draft.startYear;
  const earliest = [...draft.decades].sort()[0];
  return earliest ? earliest.replace('s', '') : '1990';
}
export function RegistrationComplete({
  draft,
  onStartOver,
  onDone
}: RegistrationCompleteProps) {
  const decade = decadeFor(anchor(draft));
  const cohort = membersFromDecade(anchor(draft));
  const isPublic = draft.visibility === 'public';
  return <div className="flex min-h-screen w-full flex-col bg-canvas lg:grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)]" data-id="element-489" data-mp="733totdp3mrs">
      <div className="h-[38vh] min-h-56 w-full lg:sticky lg:top-0 lg:h-screen" data-id="element-490" data-mp="1wfyztgfl4mic">
        <PhotoSide image={PANEL_PHOTOS.voices} caption={{
        eyebrow: `The ${decade}`,
        line: `You’re one of ${cohort + 1} people here from the ${decade}.`,
        names: RECENT_ARRIVALS[decade] ?? []
      }} data-id="element-491" data-mp="21g3f534rbb6z" />
      </div>

      <div className="flex flex-1 flex-col justify-center px-6 py-12 sm:px-10 lg:px-16" data-id="element-492" data-mp="1q21sdkmxgwkt">
        <div className="flex w-full max-w-xl flex-col gap-7" data-id="element-493" data-mp="1l3qi58sq0k22">
          <div className="flex flex-col gap-3" data-id="element-494" data-mp="2ipxl6e9dm4cb">
            <span className="text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-495" data-mp="2ipnvysw5o17o">
              You&apos;re in
            </span>
            <h1 className="text-page-title font-semibold text-ink sm:text-display" data-id="element-496" data-mp="290e0r460m2fc">
              Welcome back, {draft.firstName.trim() || 'friend'}.
            </h1>
            <p className="text-body text-ink-muted" data-id="element-497" data-mp="24114xtx61l0m">
              Check <span className="text-ink" data-id="element-498" data-mp="31gyheuj43ls6">{draft.email.trim() || 'your inbox'}</span> for a
              link to set your password. It comes from the portal, not a marketing address, and
              you can resend it from the sign-in page if it doesn&apos;t arrive.
            </p>
          </div>

          <div className="rounded-card border border-rule bg-card" data-id="element-499" data-mp="38zfx5ihkzwtl">
            <div className="flex items-center gap-4 border-b border-rule p-5" data-id="element-500" data-mp="3dzamhhlpkecj">
              <InitialAvatar name={`${draft.firstName} ${draft.lastName}`} size={52} data-id="element-501" data-mp="3e1dol004xxxm" />
              <div className="flex flex-col gap-0.5" data-id="element-502" data-mp="3izow342yjwco">
                <span className="text-heading font-semibold text-ink" data-id="element-503" data-mp="3ny03l85s5urq">{publicName(draft)}</span>
                <span className="text-small text-ink-muted" data-id="element-504" data-mp="3ryk14cu9n0ee">
                  {yearsLabel(draft)} ·{' '}
                  {isPublic ? 'findable by members' : 'visible only to you'}
                </span>
              </div>
            </div>

            <dl className="flex flex-col gap-4 p-5 text-small" data-id="element-505" data-mp="5iz9q64psonk">
              <div className="flex items-start gap-3" data-id="element-506" data-mp="ajdbx7jmx4do">
                {isPublic ? <EyeIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary-text" data-id="element-507" data-mp="fh5fiytn6exp" /> : <LockIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary-text" data-id="element-508" data-mp="1c80isnxq48zc" />}
                <div data-id="element-509" data-mp="179p685g1nkdf">
                  <dt className="font-medium text-ink" data-id="element-510" data-mp="1784m79rcvjtt">
                    {isPublic ? 'Members from your years can find you' : 'Nobody can see you yet'}
                  </dt>
                  <dd className="text-ink-muted" data-id="element-511" data-mp="1h0k4k86keaf8">
                    First name and last initial only. One tap to change, whenever you like.
                  </dd>
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-512" data-mp="od7idz4bg4mg">
                <MailIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary-text" data-id="element-513" data-mp="12b4fcwmomd1n" />
                <div data-id="element-514" data-mp="y910dy0t0qbm">
                  <dt className="font-medium text-ink" data-id="element-515" data-mp="y7pp7d514gah">
                    {draft.memory.trim() ? draft.shareMemory ? 'Your memory is with a staff member' : 'Your memory is saved privately' : 'Your contact details stay with the portal team'}
                  </dt>
                  <dd className="text-ink-muted" data-id="element-516" data-mp="2m7l2k702c209">
                    {draft.memory.trim() ? 'It joins the archive only after someone has read it, and you can withdraw it.' : 'Never shown to another member, never used to identify you.'}
                  </dd>
                </div>
              </div>
            </dl>
          </div>

          <div className="flex flex-wrap items-center gap-3" data-id="element-517" data-mp="300swi5bm99l4">
            <Button size="lg" onClick={onDone} data-id="element-518" data-mp="34x02fy40il32">
              See who else is here
            </Button>
            <Button variant="quiet" onClick={onStartOver} data-id="element-519" data-mp="2q83tlyu4t41e">
              Walk through it again
            </Button>
          </div>
        </div>
      </div>
    </div>;
}